import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Modal } from '@components/ui/Modal';
import { termsLastUpdated, termsMarkdown } from '@constants/terms';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Block =
  | { type: 'heading'; level: 1 | 2; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] };

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: var(--text-primary);
  line-height: 1.6;
`;

const Meta = styled.div`
  font-size: 0.9rem;
  color: var(--text-muted);
`;

const Heading1 = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
`;

const Heading2 = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.5rem 0 0.25rem;
`;

const Paragraph = styled.p`
  margin: 0;
  color: var(--text-secondary);
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: var(--text-secondary);

  li {
    margin-bottom: 0.35rem;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 0.5rem 0 0;
`;

const parseMarkdown = (content: string): Block[] => {
  const blocks: Block[] = [];
  const lines = content.split('\n');
  let paragraphBuffer: string[] = [];
  let listBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraphBuffer.join(' ') });
      paragraphBuffer = [];
    }
  };

  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push({ type: 'list', items: listBuffer });
      listBuffer = [];
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({
        type: 'heading',
        level: 2,
        text: trimmed.replace(/^##\s+/, ''),
      });
      return;
    }

    if (trimmed.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push({
        type: 'heading',
        level: 1,
        text: trimmed.replace(/^#\s+/, ''),
      });
      return;
    }

    if (trimmed.startsWith('- ')) {
      flushParagraph();
      listBuffer.push(trimmed.slice(2));
      return;
    }

    paragraphBuffer.push(trimmed);
  });

  flushParagraph();
  flushList();

  return blocks;
};

const extractLastUpdated = (content: string): string | undefined => {
  const match = content.match(/Última actualización:\s*(.+)/i);
  return match?.[1]?.trim();
};

export const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  const blocks = useMemo(() => parseMarkdown(termsMarkdown), []);
  const lastUpdated = useMemo(
    () => extractLastUpdated(termsMarkdown) ?? termsLastUpdated,
    [],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Términos y Condiciones"
      closeOnOverlayClick
      closeOnEscape
    >
      <Container>
        {lastUpdated && (
          <>
            <Meta>Última actualización: {lastUpdated}</Meta>
            <Divider />
          </>
        )}

        {blocks.map((block, index) => {
          if (block.type === 'heading') {
            return block.level === 1 ? (
              <Heading1 key={`${block.text}-${index}`}>{block.text}</Heading1>
            ) : (
              <Heading2 key={`${block.text}-${index}`}>{block.text}</Heading2>
            );
          }

          if (block.type === 'list') {
            return (
              <List key={`list-${index}`}>
                {block.items.map((item, itemIndex) => (
                  <li key={`list-${index}-item-${itemIndex}`}>{item}</li>
                ))}
              </List>
            );
          }

          return <Paragraph key={`paragraph-${index}`}>{block.text}</Paragraph>;
        })}
      </Container>
    </Modal>
  );
};

export default TermsModal;
